// Watch route — canonical Vue Router location builders + legacy normalizers.
//
// Single source of truth for `/watch/:kind/:id` construction so producers
// (Movies, SeriesDetail, Recordings, ContinueWatching, RecentlyAdded, and the
// next-episode jump) never assemble `/watch/...` strings independently. This
// closes the producer/consumer drift documented in
// docs/plans/watch-route-playback-contract.md (the 2026-06-15 incident where
// recordings + episode resume reached Watch and stopped at `watch.no_url`).
//
// Canonical contract (exactly three kinds):
//   Provider movie   → /watch/movie/:itemId      query: source
//   Provider episode → /watch/series/:episodeId   query: source, series
//   Recording        → /watch/recording/:id       query: none (Watch calls api.recording.fileUrl)
//
// Rules: builders never transport provider stream URLs; Watch resolves and
// tokenizes streams from source/item IDs. Legacy incoming `url` / `src` links
// are normalized by the route guard and Watch replaces them after tokenization.
// Recordings never transport a file URL in the query (the cookie-authed,
// user-scoped server route stays authoritative); episode playback always uses
// kind `series`, never `episode`. Movie/episode routes carry an optional
// `container` query param (the item's `container_extension`) so Watch can
// request the provider's real container instead of defaulting to m3u8 —
// load-bearing for episodes, which the server catalog doesn't sync.

import type { LocationQueryRaw, RouteLocationRaw } from 'vue-router';

export type WatchKind = 'movie' | 'series' | 'recording';

const CANONICAL_KINDS: readonly WatchKind[] = ['movie', 'series', 'recording'];

/**
 * Drop undefined / null / '' values so the produced query is minimal and
 * deep-equality on it is stable (consumers + tests assert exact query shape).
 */
function compactQuery(q: Record<string, string | undefined>): LocationQueryRaw {
    const out: LocationQueryRaw = {};
    for (const [k, v] of Object.entries(q)) {
        if (v !== undefined && v !== null && v !== '') out[k] = v;
    }
    return out;
}

export interface MovieRouteOptions {
    /** Provider source id — REQUIRED and must be > 0. */
    source: number;
    title?: string;
    poster?: string;
    returnPage?: string;
    /** Provider `container_extension` (e.g. 'mkv') — omitted defaults to m3u8 at Watch. */
    container?: string;
}

export function movieWatchRoute(itemId: string, opts: MovieRouteOptions): RouteLocationRaw {
    if (!itemId) throw new Error('movieWatchRoute: itemId required');
    if (!(opts.source > 0)) throw new Error('movieWatchRoute: source (>0) required');
    return {
        path: `/watch/movie/${encodeURIComponent(itemId)}`,
        query: compactQuery({
            source: String(opts.source),
            title: opts.title,
            poster: opts.poster,
            returnPage: opts.returnPage,
            container: opts.container,
        }),
    };
}

export interface EpisodeRouteOptions {
    /** Provider source id — REQUIRED and must be > 0. */
    source: number;
    /** Series id — REQUIRED (next-episode resolution + back-nav depend on it). */
    series: string;
    season?: number | string;
    title?: string;
    subtitle?: string;
    poster?: string;
    returnPage?: string;
    /** Provider `container_extension` (e.g. 'mkv') — episodes aren't synced into the
     *  server catalog, so this is the ONLY source of the real container for episodes. */
    container?: string;
}

export function episodeWatchRoute(episodeId: string, opts: EpisodeRouteOptions): RouteLocationRaw {
    if (!episodeId) throw new Error('episodeWatchRoute: episodeId required');
    if (!(opts.source > 0)) throw new Error('episodeWatchRoute: source (>0) required');
    if (!opts.series) throw new Error('episodeWatchRoute: series required');
    return {
        path: `/watch/series/${encodeURIComponent(episodeId)}`,
        query: compactQuery({
            source: String(opts.source),
            series: String(opts.series),
            season: opts.season != null ? String(opts.season) : undefined,
            title: opts.title,
            subtitle: opts.subtitle,
            poster: opts.poster,
            returnPage: opts.returnPage,
            container: opts.container,
        }),
    };
}

export interface RecordingRouteOptions {
    returnPage?: string;
}

export function recordingWatchRoute(id: string, opts: RecordingRouteOptions = {}): RouteLocationRaw {
    if (!id) throw new Error('recordingWatchRoute: id required');
    // No url / src — Watch.vue resolves the authenticated file URL via
    // api.recording.fileUrl(id). The server route (cookie-auth, user-scoped)
    // is the security boundary; the file URL never rides in route query.
    return {
        path: `/watch/recording/${encodeURIComponent(id)}`,
        query: compactQuery({ returnPage: opts.returnPage }),
    };
}

/**
 * Normalize a runtime route `:kind` segment to a canonical WatchKind.
 * Maps the legacy `episode` alias to `series`. Returns null for anything that
 * is not a recognized kind so callers never cast junk into the component union.
 */
export function normalizeWatchKind(raw: unknown): WatchKind | null {
    const k = String(raw ?? '').toLowerCase();
    if (k === 'episode') return 'series';
    return (CANONICAL_KINDS as readonly string[]).includes(k) ? (k as WatchKind) : null;
}

/** True when a query value looks like a recording file URL (`/api/recording/<id>/file`). */
export function isRecordingFileUrl(value: unknown): boolean {
    if (typeof value !== 'string' || !value) return false;
    return /\/api\/recording\/[^/]+\/file(?:[?#].*)?$/.test(value);
}

/** Minimal shape of the route bits the redirect logic reads (router-agnostic, for unit tests). */
export interface WatchRedirectInput {
    params: Record<string, unknown>;
    query: Record<string, unknown>;
}

/**
 * Pure legacy-link normalizer for the watch route's `beforeEnter` guard.
 * Returns a redirect RouteLocationRaw, or undefined to proceed unchanged.
 *
 *  - recording-shaped movie link (`/watch/movie/:id?src=/api/recording/:id/file`)
 *    → canonical `/watch/recording/:id` (file URL dropped, no open redirect).
 *  - legacy `episode` kind → canonical `/watch/series/:id` (query preserved).
 *  - unknown kind → `/home` (malformed link never reaches Watch as a bad prop).
 *  - canonical movie/series/recording → undefined (proceed).
 */
export function watchRouteRedirect(to: WatchRedirectInput): RouteLocationRaw | undefined {
    const rawKind = String(to.params.kind ?? '').toLowerCase();
    const id = String(to.params.id ?? '');

    // Recording-shaped legacy movie link: the producer used to pass the file
    // URL in `src` (or `url`). Strip it and land on the canonical recording
    // route — Watch re-derives the file URL through api.recording.fileUrl.
    if (rawKind === 'movie' && (isRecordingFileUrl(to.query.src) || isRecordingFileUrl(to.query.url))) {
        return recordingWatchRoute(id);
    }

    // `episode` is the legacy alias for `series` — preserve query verbatim.
    if (rawKind === 'episode') {
        return { path: `/watch/series/${encodeURIComponent(id)}`, query: to.query as LocationQueryRaw };
    }

    // Unknown kind — malformed link. Bounce home rather than render a dead Watch.
    if (normalizeWatchKind(rawKind) === null) {
        return { path: '/home' };
    }

    return undefined;
}
